import { useAppSettings } from "@/hooks/useAppSettings";

export interface OrderSettings {
  // General
  order_enabled: boolean;
  order_page_title: string;
  order_page_subtitle: string;
  order_submit_button_text: string;
  order_success_heading: string;
  order_success_message: string;
  order_minimum_amount: number;
  order_lead_time_days: number;

  // Fulfillment
  order_fulfillment_position: "upfront" | "checkout";
  order_pickup_enabled: boolean;
  order_delivery_enabled: boolean;
  order_delivery_collect_address_upfront: boolean;
  order_pickup_label: string;
  order_delivery_label: string;
  order_default_fulfillment: "pickup" | "delivery";

  // Delivery zone
  order_delivery_fee: string;
  order_delivery_zone_note: string;

  // Upsells
  order_upsell_section_title: string;
  order_upsell_chafing_enabled: boolean;
  order_upsell_chafing_title: string;
  order_upsell_chafing_description: string;
  order_upsell_staff_enabled: boolean;
  order_upsell_staff_title: string;
  order_upsell_staff_description: string;
  order_upsell_venue_enabled: boolean;
  order_upsell_venue_title: string;
  order_upsell_venue_description: string;

  // Checkout
  order_checkout_heading: string;
  order_disclaimer_text: string;
  order_guest_count_default: number;
  order_special_requests_placeholder: string;

  // Scheduling
  order_blackout_dates: string;
  order_available_days: string;
}

const DEFAULTS: OrderSettings = {
  order_enabled: true,
  order_page_title: "Order Drop-Off Catering",
  order_page_subtitle: "Browse our full menu, build your order, and we'll handle the rest. No payment needed now — we'll call to confirm everything.",
  order_submit_button_text: "Submit Order Request",
  order_success_heading: "Order Received!",
  order_success_message: "We'll be in touch within 24 hours to confirm details and finalize pricing.",
  order_minimum_amount: 0,
  order_lead_time_days: 2,

  order_fulfillment_position: "checkout",
  order_pickup_enabled: true,
  order_delivery_enabled: true,
  order_delivery_collect_address_upfront: false,
  order_pickup_label: "Pick Up",
  order_delivery_label: "Delivery",
  order_default_fulfillment: "pickup",

  order_delivery_fee: "quote",
  order_delivery_zone_note: "Delivery charges may apply based on location.",

  order_upsell_section_title: "Make it even better",
  order_upsell_chafing_enabled: true,
  order_upsell_chafing_title: "Add chafing dishes",
  order_upsell_chafing_description: "Keep everything warm and ready to serve. We'll include pricing when we confirm your order.",
  order_upsell_staff_enabled: true,
  order_upsell_staff_title: "Add service staff",
  order_upsell_staff_description: "Our team sets up, replenishes dishes, and handles cleanup. Pricing discussed on confirmation call.",
  order_upsell_venue_enabled: true,
  order_upsell_venue_title: "Host at our event space",
  order_upsell_venue_description: "Did you know we have a beautiful event space in Bolton? Ask us about hosting your event with us!",

  order_checkout_heading: "Complete Your Order",
  order_disclaimer_text: "No payment required now. We'll call you within 24 hours to confirm your order, finalize pricing, and collect payment.",
  order_guest_count_default: 20,
  order_special_requests_placeholder: "Allergies, dietary needs, setup instructions...",

  order_blackout_dates: "[]",
  order_available_days: '["mon","tue","wed","thu","fri","sat","sun"]',
};

function parseBool(val: string | undefined, fallback: boolean): boolean {
  if (val === undefined) return fallback;
  return val === "true" || val === "1";
}

function parseNum(val: string | undefined, fallback: number): number {
  if (val === undefined) return fallback;
  const n = parseFloat(val);
  return isNaN(n) ? fallback : n;
}

export function useOrderSettings(): { settings: OrderSettings; isLoading: boolean } {
  const { data, isLoading } = useAppSettings();

  const g = (key: keyof OrderSettings) => data?.[key];

  const settings: OrderSettings = {
    order_enabled: parseBool(g("order_enabled"), DEFAULTS.order_enabled),
    order_page_title: g("order_page_title") ?? DEFAULTS.order_page_title,
    order_page_subtitle: g("order_page_subtitle") ?? DEFAULTS.order_page_subtitle,
    order_submit_button_text: g("order_submit_button_text") ?? DEFAULTS.order_submit_button_text,
    order_success_heading: g("order_success_heading") ?? DEFAULTS.order_success_heading,
    order_success_message: g("order_success_message") ?? DEFAULTS.order_success_message,
    order_minimum_amount: parseNum(g("order_minimum_amount"), DEFAULTS.order_minimum_amount),
    order_lead_time_days: parseNum(g("order_lead_time_days"), DEFAULTS.order_lead_time_days),

    order_fulfillment_position: (g("order_fulfillment_position") as "upfront" | "checkout") ?? DEFAULTS.order_fulfillment_position,
    order_pickup_enabled: parseBool(g("order_pickup_enabled"), DEFAULTS.order_pickup_enabled),
    order_delivery_enabled: parseBool(g("order_delivery_enabled"), DEFAULTS.order_delivery_enabled),
    order_delivery_collect_address_upfront: parseBool(g("order_delivery_collect_address_upfront"), DEFAULTS.order_delivery_collect_address_upfront),
    order_pickup_label: g("order_pickup_label") ?? DEFAULTS.order_pickup_label,
    order_delivery_label: g("order_delivery_label") ?? DEFAULTS.order_delivery_label,
    order_default_fulfillment: (g("order_default_fulfillment") as "pickup" | "delivery") ?? DEFAULTS.order_default_fulfillment,

    order_delivery_fee: g("order_delivery_fee") ?? DEFAULTS.order_delivery_fee,
    order_delivery_zone_note: g("order_delivery_zone_note") ?? DEFAULTS.order_delivery_zone_note,

    order_upsell_section_title: g("order_upsell_section_title") ?? DEFAULTS.order_upsell_section_title,
    order_upsell_chafing_enabled: parseBool(g("order_upsell_chafing_enabled"), DEFAULTS.order_upsell_chafing_enabled),
    order_upsell_chafing_title: g("order_upsell_chafing_title") ?? DEFAULTS.order_upsell_chafing_title,
    order_upsell_chafing_description: g("order_upsell_chafing_description") ?? DEFAULTS.order_upsell_chafing_description,
    order_upsell_staff_enabled: parseBool(g("order_upsell_staff_enabled"), DEFAULTS.order_upsell_staff_enabled),
    order_upsell_staff_title: g("order_upsell_staff_title") ?? DEFAULTS.order_upsell_staff_title,
    order_upsell_staff_description: g("order_upsell_staff_description") ?? DEFAULTS.order_upsell_staff_description,
    order_upsell_venue_enabled: parseBool(g("order_upsell_venue_enabled"), DEFAULTS.order_upsell_venue_enabled),
    order_upsell_venue_title: g("order_upsell_venue_title") ?? DEFAULTS.order_upsell_venue_title,
    order_upsell_venue_description: g("order_upsell_venue_description") ?? DEFAULTS.order_upsell_venue_description,

    order_checkout_heading: g("order_checkout_heading") ?? DEFAULTS.order_checkout_heading,
    order_disclaimer_text: g("order_disclaimer_text") ?? DEFAULTS.order_disclaimer_text,
    order_guest_count_default: parseNum(g("order_guest_count_default"), DEFAULTS.order_guest_count_default),
    order_special_requests_placeholder: g("order_special_requests_placeholder") ?? DEFAULTS.order_special_requests_placeholder,

    order_blackout_dates: g("order_blackout_dates") ?? DEFAULTS.order_blackout_dates,
    order_available_days: g("order_available_days") ?? DEFAULTS.order_available_days,
  };

  return { settings, isLoading };
}

export { DEFAULTS as ORDER_SETTINGS_DEFAULTS };
